import {ID, IStoryAnswerLog} from '../../constants'

export type QuizOption = {id: number; title: string; optionName?: string; queueNo?: number; trueOption?: boolean; staticOption?: boolean; trueOptionId?: number}
export type QuizQuestion = {id: number; title: string; queueNo: number; questionTypeId: number; generalOptions?: QuizOption[]; matchOptions?: QuizOption[]}
export type QuizStatus = 'correct' | 'incorrect' | 'unanswered' | 'unknown'
export const quizStatusLabels: Record<QuizStatus, string> = {correct: 'Doğru', incorrect: 'Yanlış', unanswered: 'Cevaplanmadı', unknown: 'Değerlendirilmedi'}

export function answerStatus(log: IStoryAnswerLog): Exclude<QuizStatus, 'unanswered'> {
  return Number(log.answerStatus) === 1 ? 'correct' : Number(log.answerStatus) === 2 ? 'incorrect' : 'unknown'
}

export function quizResults(questions: QuizQuestion[], logs: IStoryAnswerLog[], studentId: ID) {
  return [...questions].sort((a, b) => a.queueNo - b.queueNo).map((question, index) => {
    const answers = logs.filter(log => !log.isDeleted && String(log.userId) === String(studentId) && String(log.questionId) === String(question.id)).sort((a, b) => Number(a.id) - Number(b.id))
    // Preserve wrong attempts; an ungraded/free-text answer must never count as correct.
    const status: QuizStatus = !answers.length ? 'unanswered' : answers.some(log => answerStatus(log) === 'incorrect') ? 'incorrect' : answers.some(log => answerStatus(log) === 'unknown') ? 'unknown' : 'correct'
    return {question, number: index + 1, answers, status}
  })
}

export function quizCounts(results: ReturnType<typeof quizResults>) {
  return results.reduce((counts, result) => {counts[result.status]++; return counts}, {correct: 0, incorrect: 0, unanswered: 0, unknown: 0})
}

export function quizText(value: string | undefined) {
  return value ? new DOMParser().parseFromString(value, 'text/html').body.textContent || '' : ''
}

function optionText(option: QuizOption) {
  return [option.optionName?.toLocaleUpperCase('tr-TR'), quizText(option.title)].filter(Boolean).join(' · ')
}

export function correctAnswers(question: QuizQuestion): string[] {
  if (question.generalOptions?.length) return question.generalOptions.filter(option => option.trueOption).sort((a, b) => (a.queueNo || 0) - (b.queueNo || 0)).map(optionText)
  const options = question.matchOptions || []
  const anchors = options.filter(option => question.questionTypeId === 2 ? !option.staticOption : option.staticOption).sort((a, b) => (a.queueNo || 0) - (b.queueNo || 0))
  return anchors.map(anchor => {
    const match = options.find(option => question.questionTypeId === 2 ? option.staticOption && String(option.id) === String(anchor.trueOptionId) : !option.staticOption && String(option.trueOptionId) === String(anchor.id))
    return `${optionText(anchor)} → ${match ? optionText(match) : 'Seçenek bilgisi bulunmuyor'}`
  })
}

export function studentAnswer(question: QuizQuestion, log: IStoryAnswerLog) {
  if (log.customAnswer?.trim()) return log.customAnswer
  if (question.generalOptions?.length) {
    const selected = question.generalOptions.find(option => String(option.id) === String(log.answerOptionId))
    return selected ? optionText(selected) : 'Seçilen şık bilgisi bulunmuyor.'
  }
  const options = question.matchOptions || []
  const selected = options.find(option => String(option.id) === String(log.answerOptionId))
  const target = options.find(option => String(option.id) === String(log.matchOptionId || log.staticOptionId))
  if (question.questionTypeId === 3 && selected && target && String(selected.id) !== String(target.id)) return `${optionText(target)} → ${optionText(selected)}`
  // Older ordering logs store the selected item but not its submitted position.
  if (selected) return question.questionTypeId === 2 ? `${optionText(selected)} (seçilen sıra bilgisi bulunmuyor)` : optionText(selected)
  return 'Yanıtın seçenek bilgisi bulunmuyor.'
}
